// /admin/petpack/precios
// "Lo mismo que Análisis de catálogos, pero para PetPack": trae el precio más
// barato de ML de cada producto vía la extensión "Limpack — Precios", y calcula
// si a (más barato − $1) ganás en ML y en tu tienda (doble canal), con foto,
// aviso de regalo y link contra qué comparás.
//
// Cómputo en el front (superadmin), guarda vía RPC petpack_precio_guardar.
// Envío = LimpackFlex/PetPackFlex por zona (tu costo real de reparto).

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

// ── Economía (misma que la calculadora / edge v12) ──────────────────────────
const CFG = {
  comisionML: 13.5, mpPct: 5,
  fijo: [[500, 15], [750, 25], [1000, 40]],
  flex: [
    { k: 'verde', z: 'Verde · MVD', costo: 70, umbral: 1000 },
    { k: 'celeste', z: 'Celeste · MVD', costo: 100, umbral: 1000 },
    { k: 'azul', z: 'Azul · MVD', costo: 120, umbral: 1000 },
    { k: 'costa', z: 'Costa · Canelones', costo: 160, umbral: 2000 },
  ],
}
const RATIO_MIN = 1.2, RATIO_MAX = 6
const money = n => '$' + Math.round(Number(n) || 0).toLocaleString('es-UY')
const cf = p => { for (const [h, f] of CFG.fijo) if (p <= h) return f; return 0 }
const REGALO_RE = /(de\s+regalo|\bregalo\b|obsequio|sin\s+cargo|\bcombo\b|\b2x1\b|\bkit\b|\+\s*\d+\s*(un|u|kg|kilos?|gr?|ml|cc|l|lts?)\b|con\s+(juguete|snack|premio|comedero|correa|regalo))/i
function detectarRegalo(titulo) {
  const t = (titulo || '').toLowerCase()
  if (!t) return false
  const limpio = t.replace(/env[íi]o\s+gratis/g, ' ')
  return REGALO_RE.test(limpio) || /\bgratis\b/.test(limpio)
}
function netoML(precio, costo, zona) {
  const com = precio * CFG.comisionML / 100, fijo = cf(precio), env = precio >= zona.umbral ? zona.costo : 0
  return { com, fijo, env, neto: precio - com - fijo - env - costo }
}
function netoTi(precio, costo, zona) {
  const com = precio * CFG.mpPct / 100, env = precio >= zona.umbral ? zona.costo : 0
  return { com, env, neto: precio - com - env - costo }
}

// Elige la publicación más barata que realmente coincide (por costo plausible),
// prefiriendo el match exacto de la extensión. Devuelve {precio,titulo,url,imagen_url,lleva_regalo}.
function elegirMasBarato(resultado, costo) {
  const pubs = (resultado?.todas_las_publicaciones || []).filter(p => {
    const pr = Number(p?.precio) || 0
    if (!pr) return false
    if (costo > 0 && (pr < costo * RATIO_MIN || pr > costo * RATIO_MAX)) return false
    return true
  })
  const me = resultado?.match_exacto
  let ref = null
  if (me?.precio) {
    const foto = pubs.find(p => p.url && p.url === me.url)?.imagen_url || ''
    ref = { precio: Number(me.precio), titulo: me.titulo || '', url: me.url || '', imagen_url: foto, exacto: true }
  } else if (pubs.length) {
    const mb = [...pubs].sort((a, b) => a.precio - b.precio)[0]
    ref = { precio: Number(mb.precio), titulo: mb.titulo || '', url: mb.url || '', imagen_url: mb.imagen_url || '', exacto: false }
  }
  if (ref) ref.lleva_regalo = detectarRegalo(ref.titulo)
  return { ref, nPubs: pubs.length }
}

function analizar(resultado, prod, zona) {
  const costo = Number(prod.costo_unitario) || 0
  const { ref, nPubs } = elegirMasBarato(resultado, costo)
  if (!ref) {
    return { ml_min: null, ml_publicaciones: nPubs, precio_ml_sugerido: null, ml_ganancia: null,
      ml_margen_pct: null, tienda_ganancia: null, tienda_margen_pct: null,
      ml_recomendacion: 'sin_datos', ml_mas_barato: null,
      ml_stats: { timestamp: new Date().toISOString(), match_exacto: resultado?.match_exacto || null, publicaciones: (resultado?.todas_las_publicaciones || []).slice(0, 12) } }
  }
  const sugerido = Math.max(1, Math.round(ref.precio) - 1)
  const dM = netoML(sugerido, costo, zona), dT = netoTi(sugerido, costo, zona)
  const rec = dM.neto <= 0 ? 'ml_no' : dM.neto < 40 ? 'ml_flojo' : dM.neto < 150 ? 'ml_ok' : 'ml_bueno'
  return {
    ml_min: ref.precio, ml_publicaciones: nPubs, precio_ml_sugerido: sugerido,
    ml_ganancia: Math.round(dM.neto), ml_margen_pct: sugerido ? Math.round(dM.neto / sugerido * 1000) / 10 : null,
    tienda_ganancia: Math.round(dT.neto), tienda_margen_pct: sugerido ? Math.round(dT.neto / sugerido * 1000) / 10 : null,
    ml_recomendacion: rec, ml_mas_barato: ref,
    ml_stats: { timestamp: new Date().toISOString(), zona: zona.k, match_exacto: resultado?.match_exacto || null, publicaciones: (resultado?.todas_las_publicaciones || []).slice(0, 12) },
  }
}

const REC = {
  ml_bueno: { t: '🚀 ML da bien', c: '#065f46', b: '#d1fae5' },
  ml_ok: { t: '✅ ML ok', c: '#166534', b: '#dcfce7' },
  ml_flojo: { t: '⚠ ML flojo', c: '#78350f', b: '#fef3c7' },
  ml_no: { t: '❌ ML a pérdida', c: '#991b1b', b: '#fee2e2' },
  sin_datos: { t: '🕳 Sin datos', c: '#475569', b: '#e2e8f0' },
}

export default function PetPackPrecios() {
  const [prods, setProds] = useState([])
  const [loading, setLoading] = useState(true)
  const [zonaIdx, setZonaIdx] = useState(0)
  const [q, setQ] = useState('')
  const [fEsp, setFEsp] = useState('')
  const [sel, setSel] = useState(new Set())
  const [extOk, setExtOk] = useState(false)
  const [prog, setProg] = useState(null)
  const [msg, setMsg] = useState(null)
  const [corriendo, setCorriendo] = useState(false)
  const zona = CFG.flex[zonaIdx]

  useEffect(() => { cargar() }, [])

  // Detectar extensión
  useEffect(() => {
    function onMsg(e) {
      if (e.source !== window) return
      if (e.data?.type === 'LIMPACK_EXTENSION_READY') setExtOk(true)
    }
    window.addEventListener('message', onMsg)
    const pings = [0, 300, 800, 2000, 4000].map(d => setTimeout(() => window.postMessage({ type: 'LIMPACK_EXTENSION_CHECK' }, '*'), d))
    return () => { window.removeEventListener('message', onMsg); pings.forEach(clearTimeout) }
  }, [])

  async function cargar() {
    setLoading(true)
    const { data, error } = await supabase.rpc('petpack_admin_productos', { p_incluir_eliminados: false })
    if (error) { setMsg({ e: true, t: 'No pude leer productos: ' + error.message }); setLoading(false); return }
    setProds(Array.isArray(data) ? data : [])
    setLoading(false)
  }

  const vista = useMemo(() => {
    const t = q.trim().toLowerCase()
    return prods.filter(p =>
      (!fEsp || p.especie === fEsp) &&
      (!t || `${p.marca || ''} ${p.nombre || ''} ${p.presentacion || ''}`.toLowerCase().includes(t))
    )
  }, [prods, q, fEsp])

  function toggle(id) { const s = new Set(sel); s.has(id) ? s.delete(id) : s.add(id); setSel(s) }
  function toggleTodos() { setSel(sel.size === vista.length ? new Set() : new Set(vista.map(p => p.id))) }

  async function correr(lista) {
    if (!extOk) { setMsg({ e: true, t: 'La extensión "Limpack — Precios" no está conectada. Abrila/instalala y refrescá.' }); return }
    if (!lista.length) { setMsg({ e: true, t: 'No hay productos para actualizar.' }); return }
    setCorriendo(true)
    setMsg({ e: false, t: `Buscando precios de ${lista.length} productos vía la extensión…` })
    setProg({ i: 0, total: lista.length })
    const pendientes = new Set()

    async function onItemMsg(e) {
      if (e.source !== window) return
      const d = e.data || {}
      if (d.type === 'LIMPACK_ITEM_START') setProg({ i: d.indice, total: d.total, nombre: d.nombre })
      if (d.type === 'LIMPACK_PROGRESS') setProg(p => p ? { ...p, phase: d.phase, portal: d.portal } : null)
      if (d.type === 'LIMPACK_ITEM_LISTO' && d.resultado) {
        const prod = lista.find(x => x.id === d.item_id)
        if (prod) {
          const ml = analizar(d.resultado, prod, zona)
          const pr = supabase.rpc('petpack_precio_guardar', { p_id: prod.id, p_ml: ml })
            .then(({ error }) => { if (error) console.error('petpack_precio_guardar', error) })
          pendientes.add(pr); pr.finally(() => pendientes.delete(pr))
          setProds(prev => prev.map(x => x.id === prod.id ? { ...x, ...ml, ml_actualizado_at: new Date().toISOString() } : x))
        }
      }
      if (d.type === 'LIMPACK_BLOQUEADO') {
        setMsg({ e: true, t: `🚧 ML pidió captcha y frenó (procesados ${d.procesados}/${d.total}). Abrí mercadolibre.com.uy, resolvé el "no soy un robot", esperá unos minutos y reintentá SOLO los que faltan.` })
      }
      if (d.type === 'LIMPACK_BUSQUEDA_LISTA') {
        window.removeEventListener('message', onItemMsg)
        await Promise.all([...pendientes])
        if (!d.bloqueado) setMsg({ e: false, t: `✅ Listo: ${lista.length} productos actualizados con datos reales de ML.` })
        setProg(null); setCorriendo(false); setSel(new Set())
      }
      if (d.type === 'LIMPACK_BUSQUEDA_ERROR') {
        window.removeEventListener('message', onItemMsg)
        setMsg({ e: true, t: 'Error en la extensión: ' + d.error }); setProg(null); setCorriendo(false)
      }
    }
    window.addEventListener('message', onItemMsg)
    window.postMessage({
      type: 'LIMPACK_BUSCAR_PRECIOS',
      payload: {
        productos: lista.map(p => ({ item_id: p.id, marca: p.marca, nombre: p.nombre, presentacion: p.presentacion })),
        portales: ['MercadoLibre'],
      },
    }, '*')
  }

  const seleccionados = () => vista.filter(p => sel.has(p.id))
  const faltantes = () => vista.filter(p => !p.ml_actualizado_at)

  const kpis = useMemo(() => {
    const conDatos = prods.filter(p => p.precio_ml_sugerido != null)
    const mlOk = conDatos.filter(p => Number(p.ml_ganancia) > 0).length
    const tiOk = conDatos.filter(p => Number(p.tienda_ganancia) > 0).length
    return { conDatos: conDatos.length, mlOk, tiOk }
  }, [prods])

  return (
    <AdminLayout title="PetPack · Precios ML">
      <div style={{ padding: 24, maxWidth: 1200 }}>
        <h1 style={{ margin: 0, color: '#1a3461', fontSize: 24 }}>🐾 PetPack · Precios vs MercadoLibre</h1>
        <p style={{ color: '#64748b', marginTop: 6, fontSize: 13, maxWidth: 820, lineHeight: 1.5 }}>
          Trae el más barato de ML de cada producto (vía la extensión) y calcula si a <b>ese precio − $1</b> ganás
          en ML y en tu tienda. Envío = PetPackFlex por zona. Con foto, aviso de regalo y link contra qué comparás.
        </p>

        {/* estado extensión */}
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '4px 10px',
          background: extOk ? '#f0fdf4' : '#fef3c7', border: `1px solid ${extOk ? '#86efac' : '#fcd34d'}`,
          borderRadius: 999, fontSize: 11, fontWeight: 600, color: extOk ? '#166534' : '#78350f' }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: extOk ? '#22c55e' : '#f59e0b' }} />
          {extOk ? 'Extensión Precios conectada' : 'Extensión "Limpack — Precios" no conectada'}
        </div>

        {/* controles */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '16px 0' }}>
          <input type="search" value={q} onChange={e => setQ(e.target.value)} placeholder="🔍 Buscar producto o marca…"
            style={{ flex: 1, minWidth: 200, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
          <select value={fEsp} onChange={e => setFEsp(e.target.value)} style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}>
            <option value="">Perro y gato</option><option value="perro">Perro</option><option value="gato">Gato</option>
          </select>
          <select value={zonaIdx} onChange={e => setZonaIdx(+e.target.value)} title="Zona Flex (tu costo de reparto)"
            style={{ padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }}>
            {CFG.flex.map((z, i) => <option key={z.k} value={i}>{z.z} — {money(z.costo)}</option>)}
          </select>
        </div>

        {/* acciones */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <button onClick={() => correr(seleccionados())} disabled={corriendo || sel.size === 0}
            style={btn(sel.size > 0 && !corriendo, '#f59e0b')}>🔍 Actualizar seleccionados ({sel.size})</button>
          <button onClick={() => correr(faltantes())} disabled={corriendo}
            style={btn(!corriendo, '#00A8B5')}>⚡ Traer los que faltan ({faltantes().length})</button>
          <button onClick={() => correr(vista)} disabled={corriendo}
            style={btn(!corriendo, '#1a3461')}>🔄 Actualizar todo el filtro ({vista.length})</button>
          <span style={{ fontSize: 11, color: '#94a3b8' }}>~8s por producto. Corré en tandas si son muchos; dejá la pestaña abierta.</span>
        </div>

        {/* kpis */}
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <Kpi n={kpis.conDatos} l={`de ${prods.length} con precio ML`} />
          <Kpi n={kpis.mlOk} l="rentables en ML (a mercado − $1)" />
          <Kpi n={kpis.tiOk} l="rentables en tu tienda" />
        </div>

        {prog && (
          <div style={{ padding: 10, background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 8, fontSize: 12, marginBottom: 12, color: '#1e40af' }}>
            🔍 {prog.i}/{prog.total} {prog.nombre ? '· ' + prog.nombre : ''} {prog.phase ? '· ' + prog.phase : ''}
            <div style={{ marginTop: 6, height: 4, background: '#dbeafe', borderRadius: 999, overflow: 'hidden' }}>
              <div style={{ width: `${(prog.i / prog.total) * 100}%`, height: '100%', background: '#3b82f6' }} />
            </div>
          </div>
        )}
        {msg && <div style={{ padding: 10, marginBottom: 12, borderRadius: 6, fontSize: 12,
          background: msg.e ? '#fee2e2' : '#d1fae5', border: `1px solid ${msg.e ? '#fca5a5' : '#86efac'}`, color: msg.e ? '#991b1b' : '#065f46' }}>{msg.t}</div>}

        {/* tabla */}
        <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 900 }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0', fontSize: 10, textTransform: 'uppercase', color: '#64748b' }}>
                <th style={{ padding: 8, width: 30 }}><input type="checkbox" checked={sel.size === vista.length && vista.length > 0} onChange={toggleTodos} /></th>
                <th style={th('left')}>Producto</th>
                <th style={th('right')}>Costo</th>
                <th style={th('right')}>Más barato ML</th>
                <th style={th('right')}>Tu precio (−$1)</th>
                <th style={th('right')}>Ganás ML</th>
                <th style={th('right')}>Ganás tienda</th>
                <th style={th('left')}>Veredicto</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Cargando…</td></tr>
              : vista.length === 0 ? <tr><td colSpan={8} style={{ padding: 20, textAlign: 'center', color: '#94a3b8' }}>Sin productos.</td></tr>
              : vista.map(p => {
                const rec = REC[p.ml_recomendacion] || null
                const mb = p.ml_mas_barato
                return (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: 8, textAlign: 'center' }}><input type="checkbox" checked={sel.has(p.id)} onChange={() => toggle(p.id)} /></td>
                    <td style={{ padding: 8 }}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                        {mb?.imagen_url && <a href={mb.url || '#'} target="_blank" rel="noopener"><img src={mb.imagen_url} alt="" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 6, border: '1px solid #e2e8f0' }} /></a>}
                        <div>
                          <div style={{ fontWeight: 700, color: '#1a3461' }}>{p.marca ? p.marca + ' · ' : ''}{p.nombre}</div>
                          <div style={{ fontSize: 10.5, color: '#64748b' }}>
                            {p.presentacion} · {p.especie}
                            {mb?.lleva_regalo && <span style={{ marginLeft: 6, background: '#fef3c7', color: '#78350f', padding: '1px 6px', borderRadius: 999, fontWeight: 700 }}>🎁 con regalo</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={td()}>{money(p.costo_unitario)}</td>
                    <td style={td()}>
                      {p.ml_min != null ? <>
                        <div style={{ fontWeight: 700 }}>{money(p.ml_min)}</div>
                        {mb?.url && <a href={mb.url} target="_blank" rel="noopener" style={{ fontSize: 10, color: '#0ea5e9', fontWeight: 700, textDecoration: 'none' }}>comparo contra ↗</a>}
                        {mb && !mb.exacto && <div style={{ fontSize: 9, color: '#94a3b8' }}>aprox. ({p.ml_publicaciones} publ.)</div>}
                      </> : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={td()}>{p.precio_ml_sugerido != null ? <b>{money(p.precio_ml_sugerido)}</b> : '—'}</td>
                    <td style={{ ...td(), color: p.ml_ganancia > 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{p.ml_ganancia != null ? money(p.ml_ganancia) : '—'}{p.ml_margen_pct != null ? <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 400 }}>{p.ml_margen_pct}%</div> : null}</td>
                    <td style={{ ...td(), color: p.tienda_ganancia > 0 ? '#16a34a' : '#dc2626', fontWeight: 700 }}>{p.tienda_ganancia != null ? money(p.tienda_ganancia) : '—'}{p.tienda_margen_pct != null ? <div style={{ fontSize: 9, color: '#94a3b8', fontWeight: 400 }}>{p.tienda_margen_pct}%</div> : null}</td>
                    <td style={{ padding: 8 }}>
                      {rec ? <span style={{ padding: '3px 8px', background: rec.b, color: rec.c, borderRadius: 999, fontSize: 10, fontWeight: 700 }}>{rec.t}</span> : <span style={{ color: '#cbd5e1', fontSize: 11 }}>sin analizar</span>}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <p style={{ fontSize: 11, color: '#64748b', marginTop: 10, lineHeight: 1.5 }}>
          "Ganás" = neto por unidad a mercado − $1, ya sin comisión ({CFG.comisionML}% ML / {CFG.mpPct}% MP) + costo fijo MELI + Flex ({zona.z}). Rojo = a ese precio perdés.
          IVA sobre la comisión es un ajuste fino que no se resta. Verificá la foto: si es otra presentación o lleva regalo, no es comparable.
        </p>
      </div>
    </AdminLayout>
  )
}

function th(align) { return { padding: 8, textAlign: align } }
function td() { return { padding: 8, textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }
function btn(on, color) { return { padding: '8px 14px', background: on ? color : '#94a3b8', color: '#fff', border: 0, borderRadius: 8, fontWeight: 700, fontSize: 12.5, cursor: on ? 'pointer' : 'not-allowed' } }
function Kpi({ n, l }) {
  return <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '8px 14px' }}>
    <div style={{ fontSize: 20, fontWeight: 900, color: '#1a3461' }}>{n}</div>
    <div style={{ fontSize: 10.5, color: '#64748b' }}>{l}</div>
  </div>
}
